import { isDefined } from '@sgme/fp';
import classNames from 'classnames';
import type { IntlKey } from 'locales';
import { createContext, type ReactNode, useContext } from 'react';
import { useIntl } from 'react-intl';

import { FormGroupValidationFeedbackMessage } from './FormGroupValidationFeedbackMessage';

export interface VerticalFormGroupProps<IdType extends string = string> {
  children: ReactNode;
  className?: string;
  fieldsClassName?: string;
  inputId?: IdType;
  label?: IntlKey;
  labelNode?: ReactNode;
}

export const ModifiedFieldsContext = createContext<readonly string[] | undefined>(undefined);

export function VerticalFormGroupLabel<IdType extends string>({
  inputId,
  label,
  labelNode,
}: Pick<VerticalFormGroupProps<IdType>, 'inputId' | 'label' | 'labelNode'>) {
  const { formatMessage } = useIntl();
  return (
    <label className="form-label text-start text-secondary w-100 d-flex mb-2" id={`${inputId}-label`} htmlFor={inputId}>
      {labelNode ?? (isDefined(label) ? formatMessage({ id: label }) : null)}
    </label>
  );
}

function hasInputInModifiedFields<IdType extends string>(
  modifiedFields: readonly string[] | undefined | null,
  inputId: IdType | undefined,
): boolean {
  if (inputId !== undefined && modifiedFields !== undefined && modifiedFields !== null) {
    return modifiedFields.includes(inputId);
  }
  return false;
}

export function VerticalFormGroupInput<IdType extends string>({
  inputId,
  fieldsClassName,
  children,
}: Pick<VerticalFormGroupProps<IdType>, 'inputId' | 'children' | 'fieldsClassName'>) {
  const modifiedFields = useContext(ModifiedFieldsContext);
  const isModified = hasInputInModifiedFields<IdType>(modifiedFields, inputId);
  return <div className={classNames(fieldsClassName, { 'border border-info': isModified })}>{children}</div>;
}

export function VerticalFormGroupMessage<IdType extends string>({
  inputId,
}: Pick<VerticalFormGroupProps<IdType>, 'inputId'>) {
  if (inputId !== undefined) {
    return <FormGroupValidationFeedbackMessage inputId={inputId} />;
  }

  return null;
}

export function VerticalFormGroup<IdType extends string>({
  className,
  fieldsClassName,
  inputId,
  label,
  children,
  labelNode,
}: VerticalFormGroupProps<IdType>) {
  return (
    <div className={classNames(className, 'mb-2')}>
      <VerticalFormGroupLabel inputId={inputId} labelNode={labelNode} label={label} />
      <VerticalFormGroupContent inputId={inputId} fieldsClassName={fieldsClassName}>
        {children}
      </VerticalFormGroupContent>
    </div>
  );
}

type VerticalFormGroupContentProps<IdType extends string> = Pick<
  VerticalFormGroupProps<IdType>,
  'fieldsClassName' | 'inputId' | 'children'
>;

export function VerticalFormGroupContent<IdType extends string>({
  fieldsClassName,
  inputId,
  children,
}: VerticalFormGroupContentProps<IdType>) {
  return (
    <>
      <VerticalFormGroupInput inputId={inputId} fieldsClassName={fieldsClassName}>
        {children}
      </VerticalFormGroupInput>
      <VerticalFormGroupMessage inputId={inputId} />
    </>
  );
}
